import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FREIGHT_FORWARDING_VERTICAL } from '@configs/constants';
import { doesHaveOneOfScopes, SCOPES } from '@configs/scopes';
import { AttachmentModel } from '@core/models/attachment.model';
import { AuthService } from '@core/services/auth/auth.service';
import { NavigationHistoryService } from '@core/services/navigation-history.service';
import { NotificationService } from '@core/services/notification/notification.service';
import {
  StopListeningForAllKeyedEntityUpdates,
  StopListeningForEntityUpdates,
} from '@core/store/entity-updates/entity-updates.actions';
import { StopListeningForJobUpdates } from '@core/store/job-updates/job-updates.actions';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  isRequireMfa,
  isRequireMfaSetup,
  isRequirePasswordReset,
  isTokenObject,
  LoginResponse,
} from 'app/modules/auth/utils/check-login-response.util';
import { Operation } from 'app/modules/organizations-manager/models/operation.model';
import { OrganizationService } from 'app/modules/organizations-manager/services/organizations-manager.service';
import { jwtDecode } from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';
import { of } from 'rxjs';
import { catchError, finalize, first, map, take, tap } from 'rxjs/operators';
import { User } from '../../models/user.model';
import { SetOrganizationEntitlementsCapabilityData, SetSpinner } from '../shared/shared.actions';
import {
  AutoLoginAction,
  AutoLoginFailed,
  AutoLoginSuccess,
  Login,
  LoginFailed,
  LoginOtpVerifiedUser,
  LoginSelfSignedUpUser,
  LoginSuccess,
  Logout,
  PasswordResetAction,
  PasswordResetRequestAction,
  RefreshToken,
  ResetLoginMessage,
  SetToken,
  SetUser,
} from './auth.actions';

export const authStateKey = 'auth';
const ONE_MILLISECOND = 1000;
export class AuthStateModel {
  public token: string;
  public user: User;
  public userScopes: Record<string, string>;
  public message: string;
  public error: boolean;
  public logo: AttachmentModel;
  public currentUserOperations: Operation[];
  public selectedOperation: Operation;
  public isLoading: boolean;
}

@State<AuthStateModel>({
  name: authStateKey,
  defaults: {
    token: '',
    user: null,
    userScopes: {},
    message: '',
    error: false,
    logo: null,
    currentUserOperations: [],
    selectedOperation: null,
    isLoading: false,
  },
})
@Injectable()
export class AuthState {
  // Guards against duplicate logouts. A single failed refresh is reacted to by
  // BOTH the RefreshToken action's catchError and the HTTP interceptor, and a
  // burst of concurrent 401s can each start their own refresh->logout cycle.
  // Without this, every one of those fires a separate /logout request.
  private isLoggingOut = false;

  constructor(
    private cookieService: CookieService,
    private authService: AuthService,
    private orgService: OrganizationService,
    private router: Router,
    private notificationService: NotificationService,
    private navigationHistory: NavigationHistoryService
  ) {}

  @Selector()
  static token(state: AuthStateModel): string | null {
    return state.token;
  }

  @Selector()
  static isAuthenticated(state: AuthStateModel): boolean {
    return !!state.token;
  }

  @Selector()
  static getUser(state: AuthStateModel): User {
    return state.user;
  }

  @Selector()
  static getUserFullName(state: AuthStateModel): string {
    return state.user?.firstName + ' ' + state.user?.lastName;
  }

  @Selector()
  static getLogo(state: AuthStateModel): AttachmentModel {
    return state.logo;
  }

  @Selector()
  static getOrgId(state: AuthStateModel): string {
    return state.user?.orgId;
  }

  @Selector()
  static getOrgUserId(state: AuthStateModel): string {
    return state.user?.userId;
  }

  @Selector()
  static getErrorMessage(state: AuthStateModel): string | null {
    return state.message || null;
  }

  @Selector()
  static hasError(state: AuthStateModel): boolean {
    return state.error;
  }

  @Selector()
  static getCurrentUserOperations(state: AuthStateModel): Operation[] {
    return [...state.currentUserOperations];
  }

  // this used to get user operation names by ids
  @Selector()
  static getCurrentOperations(state: AuthStateModel): string[] {
    return [...state.user.operations];
  }

  @Selector()
  static getSelectedOperation(state: AuthStateModel): Operation {
    if (state.user.operations.length > 1) {
      return { _id: '', name: 'All' };
    }
    return state.currentUserOperations.find(op => op._id === state.user?.operations[0]);
  }

  @Selector()
  static getUserScopes(state: AuthStateModel) {
    return state.user.scopes;
  }

  @Selector()
  static getUserScopesMap(state: AuthStateModel) {
    return state.userScopes;
  }

  @Selector()
  static getIsSuperAdmin(state: AuthStateModel) {
    return doesHaveOneOfScopes(state.user.scopes, [SCOPES.ADMIN.SUPER]);
  }

  @Selector()
  static loggedInOrgVerticals(state: AuthStateModel) {
    return state.user?.orgVerticals || [];
  }

  @Selector()
  static isFreightForwarder(state: AuthStateModel) {
    return state.user?.orgVerticals?.includes(FREIGHT_FORWARDING_VERTICAL) ?? false;
  }

  @Selector()
  static loggedInUsersOrganization(state: AuthStateModel) {
    return state.user?.orgId;
  }

  @Selector()
  static isLoading(state: AuthStateModel) {
    return state.isLoading;
  }

  @Action(SetUser)
  setUser({ patchState }: StateContext<AuthStateModel>, { user }: SetUser) {
    return of(patchState({ user }));
  }

  @Action(SetToken)
  setToken({ patchState }: StateContext<AuthStateModel>, { token }: SetToken) {
    patchState({ token });
  }

  @Action(AutoLoginAction)
  autoLogin({ dispatch, patchState }: StateContext<AuthStateModel>) {
    patchState({ token: null, user: null, message: null, logo: null, error: false });

    const token = this.cookieService.get('token');
    if (!token) {
      dispatch([new AutoLoginFailed(), new SetSpinner(false)]);
      return;
    }
    try {
      const decodedToken = jwtDecode(token);
      const exp = decodedToken[`exp`] as number;

      if (exp && Date.now() > exp * ONE_MILLISECOND) {
        console.log('token expired, Auto login failed');
        dispatch([new AutoLoginFailed(), new SetSpinner(false)]);
      }
      // auto login success
      const user: User = decodedToken as User;
      user.userId = decodedToken[`orgUserId`];
      // TODO: remove this, and get it from the token, currently this is not in the token
      patchState({
        token,
        user,
        message: 'Login Successful',
        logo: null,
        userScopes: this.getUserScopesMap(user.scopes),
      });
      dispatch([new AutoLoginSuccess(), new SetSpinner(false)]);
    } catch (_e) {
      dispatch([new AutoLoginFailed(), new SetSpinner(false)]);
    }
  }

  @Action(Login)
  login({ dispatch, patchState }: StateContext<AuthStateModel>, loginAction: Login) {
    patchState({ isLoading: true });
    return this.authService
      .login(loginAction)
      .pipe(
        first(),
        tap(loginResponse => {
          // this.handleLoginToken(loginResponse, patchState, dispatch);
          if (isTokenObject(loginResponse)) {
            // dispatch([new LoginSuccess(loginAction.redirectUrl), new SetSpinner(false)]);
            const decodedToken = jwtDecode(loginResponse.accessToken);
            const user: User = decodedToken as User;
            user.userId = decodedToken[`orgUserId`];

            patchState({
              token: loginResponse.accessToken,
              user,
              message: 'Login Successful',
              logo: null,
              userScopes: this.getUserScopesMap(user.scopes),
            });
            //this.cookieService.deleteAll('token');
            //this.cookieService.set('token', loginResponse.accessToken, decodedToken[`exp`] * ONE_MILLISECOND, '/');
            localStorage.setItem('currentUser', JSON.stringify(user));
            //localStorage.setItem('refreshToken', loginResponse.refreshToken);

            this.orgService
              .getOrganizationInformation(user.orgId)
              .pipe(take(1))
              .subscribe(({ orgEntitlements, organizationCapabilityPlanByOrgId, getOrganizationById }) => {
                patchState({
                  user: {
                    ...user,
                    orgVerticals: getOrganizationById.verticals,
                  },
                });
                dispatch([
                  new LoginSuccess(
                    loginAction.redirectUrl ??
                      getOrganizationById.landingPageUrl ??
                      organizationCapabilityPlanByOrgId.defaultHomePageUrl
                  ),
                  new SetSpinner(false),
                  new SetOrganizationEntitlementsCapabilityData(orgEntitlements, organizationCapabilityPlanByOrgId),
                ]);
              });
          } else if (isRequireMfa(loginResponse)) {
            this.router.navigate(['/auth/mfa-verify'], {
              queryParams: {
                mfaToken: loginResponse.mfaToken,
                mfaMethod: loginResponse.mfaMethod,
                redirect: loginAction.redirectUrl || '',
              },
            });
          } else if (isRequireMfaSetup(loginResponse)) {
            this.router.navigate(['/auth/activate/mfa-setup'], {
              queryParams: {
                userId: loginResponse.userId,
                email: loginResponse.email,
                mfaToken: loginResponse.mfaToken,
                loginFlow: 'true',
              },
            });
          } else if (isRequirePasswordReset(loginResponse) && loginResponse.tooManyAttempts) {
            dispatch([
              new LoginFailed('Too many attempts to reset password today, please try again later'),
              new SetSpinner(false),
            ]);
          } else if (
            isRequirePasswordReset(loginResponse) &&
            loginResponse.requirePasswordReset &&
            !loginResponse.tooManyAttempts
          ) {
            this.router.navigate([`/auth/first-login`], {
              queryParams: {
                userId: loginAction.username,
              },
            });
          }
        }),
        catchError(err => {
          const typedError = err as HttpErrorResponse;
          return dispatch([new LoginFailed(typedError.error.errorMessage)]);
        })
      )
      .subscribe({
        complete: () => {
          patchState({ isLoading: false });
        },
      });
  }

  @Action(RefreshToken)
  refresh({ dispatch, patchState }: StateContext<AuthStateModel>, action: RefreshToken) {
    dispatch(new SetSpinner(true));
    return this.authService.refreshToken().pipe(
      tap(loginResponse => {
        if (isTokenObject(loginResponse)) {
          this.handleLoginToken(loginResponse, patchState, dispatch);
        }
      }),
      catchError(err => {
        console.error('Refresh token failed:', err);
        dispatch(new Logout());
        return of(null);
      }),
      finalize(() => {
        dispatch(new SetSpinner(false));
      })
    );
  }

  handleLoginToken(
    loginResponse: LoginResponse,
    patchState: (val: Partial<AuthStateModel>) => AuthStateModel,
    dispatch: (actions: unknown) => unknown
  ) {
    if (isTokenObject(loginResponse)) {
      const decodedToken = jwtDecode(loginResponse.accessToken);
      const user: User = decodedToken as User;
      user.userId = decodedToken[`orgUserId`];

      patchState({
        token: loginResponse.accessToken,
        user,
        message: 'Login Successful',
        logo: null,
        userScopes: this.getUserScopesMap(user.scopes),
      });
      //this.cookieService.deleteAll('token');
      //this.cookieService.set('token', loginResponse.accessToken, decodedToken[`exp`] * ONE_MILLISECOND, '/');
      localStorage.setItem('currentUser', JSON.stringify(user));
      //localStorage.setItem('refreshToken', loginResponse.refreshToken);

      this.orgService
        .getOrganizationInformation(user.orgId)
        .pipe(take(1))
        .subscribe(({ orgEntitlements, organizationCapabilityPlanByOrgId, getOrganizationById }) => {
          patchState({
            user: {
              ...user,
              orgVerticals: getOrganizationById.verticals,
            },
          });
          dispatch([
            new LoginSuccess(organizationCapabilityPlanByOrgId.defaultHomePageUrl),
            new SetSpinner(false),
            new SetOrganizationEntitlementsCapabilityData(orgEntitlements, organizationCapabilityPlanByOrgId),
          ]);
        });
    }
  }

  @Action(LoginFailed)
  processLoginFailure({ dispatch, patchState }: StateContext<AuthStateModel>, { errorMsg }: LoginFailed) {
    dispatch([new SetSpinner(false)]);
    return patchState({
      token: null,
      user: null,
      userScopes: {},
      error: true,
      message: errorMsg ?? 'Something went wrong, please try again later',
      logo: null,
    });
  }

  @Action(ResetLoginMessage)
  resetLoginMessage({ patchState }: StateContext<AuthStateModel>) {
    return patchState({ message: null, error: false, isLoading: false });
  }

  @Action(Logout)
  logout({ patchState, dispatch }: StateContext<AuthStateModel>) {
    // Idempotency guard: concurrent 401s and the interceptor/RefreshToken
    // double-dispatch must collapse into a single /logout call + redirect.
    if (this.isLoggingOut) {
      return of(null);
    }
    this.isLoggingOut = true;

    const clearSessionAndRedirect = () => {
      localStorage.clear();
      this.cookieService.delete('token', '/');
      this.navigationHistory.clearCache();
      patchState({
        message: null,
        token: null,
        user: null,
        userScopes: {},
        logo: null,
        error: false,
      });
      this.router.navigate(['/auth/login']);
    };

    // Directly dispatch actions to stop Firebase listeners
    dispatch([
      new StopListeningForJobUpdates(),
      new StopListeningForEntityUpdates(),
      new StopListeningForAllKeyedEntityUpdates(),
    ]);

    // Existing logout code
    return this.authService.logout().pipe(
      tap(() => clearSessionAndRedirect()),
      catchError(() => {
        clearSessionAndRedirect();
        return of(null);
      }),
      finalize(() => {
        this.isLoggingOut = false;
      })
    );
  }

  @Action(PasswordResetRequestAction)
  sendPasswordResetRequest({ dispatch }: StateContext<AuthStateModel>, { userId }: PasswordResetRequestAction) {
    return this.authService.initResetPassword(userId, false).pipe(
      map(r => {
        this.router.navigate(['/auth/reset-password-email-sent-confirmation'], {
          queryParams: {
            userId,
          },
        });
      }),
      catchError(_err => {
        console.log(_err);
        this.notificationService.showError('Failed to send email, please try again later');
        return of(null);
      })
    );
  }

  @Action(PasswordResetAction)
  resetPassword(
    { dispatch }: StateContext<AuthStateModel>,
    { newPassword, reTypeNewPassword, resetKey, firstLogin }: PasswordResetAction
  ) {
    return this.authService
      .resetPassword({ newPassword, confirmPassword: reTypeNewPassword, resetKey }, firstLogin)
      .pipe(first())
      .subscribe({
        next: () => {
          this.notificationService.showSuccess('Password reset successfully, login to continue');
          this.router.navigate(['/auth/login']);
        },
        error: e => {
          console.log(e);
          this.notificationService.showError('Failed to reset password, please try again later');
        },
      });
  }

  @Action(LoginSelfSignedUpUser)
  loginSelfSignedUpUser(
    { patchState, dispatch }: StateContext<AuthStateModel>,
    { accessToken }: LoginSelfSignedUpUser
  ) {
    this.handleLoginToken({ accessToken }, patchState, dispatch);
  }

  @Action(LoginOtpVerifiedUser)
  loginOtpVerifiedUser(
    { dispatch, patchState }: StateContext<AuthStateModel>,
    { accessToken, refreshToken }: LoginOtpVerifiedUser
  ) {
    try {
      // Decode the JWT token to get user information
      const decodedToken = jwtDecode(accessToken);
      const user: User = decodedToken as User;
      user.userId = decodedToken[`orgUserId`];

      // Update the state with token and user information
      patchState({
        token: accessToken,
        user,
        message: 'OTP Verification Successful',
        logo: null,
        error: false,
        userScopes: this.getUserScopesMap(user.scopes),
      });

      // Set cookie with token expiration
      //this.cookieService.set('token', accessToken, decodedToken[`exp`] * ONE_MILLISECOND, '/');

      // Store user and refresh token in localStorage
      localStorage.setItem('currentUser', JSON.stringify(user));
      //localStorage.setItem('refreshToken', refreshToken);

      this.orgService
        .getOrganizationInformation(user.orgId)
        .pipe(take(1))
        .subscribe(({ orgEntitlements, organizationCapabilityPlanByOrgId, getOrganizationById }) => {
          patchState({
            user: {
              ...user,
              orgVerticals: getOrganizationById.verticals,
            },
          });
          dispatch([
            new LoginSuccess(
              getOrganizationById.landingPageUrl ?? organizationCapabilityPlanByOrgId.defaultHomePageUrl
            ),
            new SetSpinner(false),
            new SetOrganizationEntitlementsCapabilityData(orgEntitlements, organizationCapabilityPlanByOrgId),
          ]);
        });

      // Fetch organization verticals for the user
      // this.orgService
      //   .getOrganizationVerticalsById(user.orgId)
      //   .pipe(take(1))
      //   .subscribe(verticals => {
      //     patchState({
      //       user: {
      //         ...user,
      //         orgVerticals: verticals,
      //       },
      //     });
      //   });
    } catch (error) {
      console.error('Error processing OTP verified user login:', error);
      patchState({
        token: null,
        user: null,
        error: true,
        userScopes: {},
        message: 'Failed to process login after OTP verification',
        logo: null,
      });
    }
  }

  private getUserScopesMap(scopes: string[]): Record<string, string> {
    return scopes.reduce((prev, curr) => {
      prev[curr] = curr;
      return prev;
    }, {});
  }
}
